import { routes } from "./routes.js"
import { createRouter, createWebHistory } from "vue-router"

import { useAuthStore } from "/src/stores/auth"
import { useCreditsStore } from "/src/stores/credits"
import * as Sentry from "@sentry/vue"
import { storeToRefs } from "pinia"
import { isMobileDevice } from "/src/utils/device"

const router = createRouter({
    history: createWebHistory(),
    routes,
})

const addMetaTags = (metaTags) => {
    const VUE_META_TAGS_ATTR = "data-vue-meta"

    // Remove tags that were added in other views
    Array.from(document.querySelectorAll(`[${VUE_META_TAGS_ATTR}]`)).map((el) =>
        el.parentNode.removeChild(el)
    )

    const metaTagsArray = Object.entries(metaTags || {})
    metaTagsArray
        .map(([metaTagKey, metaTagValue]) => {
            const tag = document.createElement("meta")
            tag.setAttribute(metaTagKey, metaTagValue)
            tag.setAttribute(VUE_META_TAGS_ATTR, "")

            return tag
        }) // Add the meta tags to the document head.
        .forEach((tag) => document.head.appendChild(tag))
}

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()
    const {
        isLoggedIn,
        isRegistrationComplete,
        isSuperAdmin,
        isAdmin,
        currentOrganization,
        currentUser,
    } = storeToRefs(authStore)

    addMetaTags(to.meta.metaTags)

    // Retrieve user if not there (NOTE: the store is cleared on page refresh)
    if (!isLoggedIn.value) {
        try {
            await authStore.retrieveUser()
        } catch (error) {
            return to.name === "login" ||
                to.name === "self-service-registration"
                ? next()
                : next({ name: "login" })
        }
    }

    // If logged in and a mobile device is detected, redirect to the unsupported page,
    // except for registration flows.
    // you could add a allowedOnMobile meta attribute to the routes that can be viewed on mobile, 
    // so you don't need to update this list whenever we make any view compatible with mobile.
    if (
        isLoggedIn.value &&
        isMobileDevice() &&
        !["mobile-unsupported", "complete-registration"].includes(to.name)
    )
        return next({ name: "mobile-unsupported" })

    // User is logged in, so we can check their credits balance.
    const creditsStore = useCreditsStore()
    creditsStore.updateCurrentBalance()

    // CHECK USER REGISTRATION IS COMPLETE
    if (!isRegistrationComplete.value && to.name !== "complete-registration") {
        // If the user hasn't completed registration or does not belong to any project,
        //  then redirect to registration view
        return next({ name: "complete-registration" })
    } else if (
        isRegistrationComplete.value &&
        to.name === "complete-registration"
    ) {
        // If the user tries to access registration but is already registered, redirect
        // them home.
        return next({ name: "home" })
    } else if (!currentUser.value.is_active && to.name !== "inactive-user") {
        // If the user is not active, always redirect to the inactive-user page
        return next({ name: "inactive-user" })
    } else if (to.name === "inactive-user" && currentUser.value.is_active) {
        // If the user is acrive they can't visit the inactive-user page
        return next({ name: "home" })
    } else if (
        to.name === "billing-required" &&
        (currentOrganization.value?.subscription_status == "active" ||
            currentOrganization.value?.subscription_status == "trialing")
    ) {
        // If billing is set up or free trial is currently in progress, user can't visit
        // the billing-required page
        return next({ name: "home" })
    }

    if (to.meta.requiresAuth) {
        // Assertions
        if (!currentUser.value)
            throw "User must be already specified at this point"

        // Add user/organization details to sentry errors
        Sentry.setUser({
            email: currentUser.value.email,
            id: currentUser.value.id,
        })
        if (currentOrganization.value) {
            Sentry.setTags({
                organization: currentOrganization.value.name,
                organization_id: currentOrganization.value.id,
            })
        }

        // If trial has expired and billing is not set up for the current organization,
        // redirect user to billing page.
        const freeTrialExpired =
            currentOrganization.value.subscription_status == "trialing" &&
            currentOrganization.value.has_free_trial_expired

        const invalidSubscription = ![null, "active", "trialing"].includes(
            currentOrganization.value.subscription_status
        )
        if (
            to.name == "documents-list" &&
            (freeTrialExpired || invalidSubscription)
        ) {
            // If the user is not an admin, then they can't setup the billing by themselves
            //  so they are redirected to a dedicated page.
            if (!isAdmin.value) return next({ name: "billing-required" })
            // Admins can check/edit billing.
            return next({ name: "billing" })
        }

        // If user is trying to access an organization-admin only page then redirect them home
        if (to.meta.requiresAdmin && !isAdmin.value)
            return next({ name: "home" })

        // If user is trying to access a super-admin only page then redirect them home
        if (to.meta.requiresSuperAdmin && !isSuperAdmin.value)
            return next({ name: "home" })

        // Else go to the requested view
        next()
    } else if (to.meta.requiresAnonymous && isLoggedIn) {
        // Some routes, e.g. login, require the user not to be logged in. If
        //  a logged in user tries to acess them, redirect to the home page.
        next({ name: "home" })
    } else next()
})

export default router
